import React from 'react';

import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { NoActivitiesEmptyState } from '../../EmptyStates';
import LoadingState from '../../LoadingState';
import { ROUTES } from '../../../routes';
import type { Activity } from '../types';

import { ActivityItem } from './ActivityItem';

type ActivitiesListProps = {
  activities: Activity[];
  currentUserId?: number | null;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onReturnToQueue?: (activityId: number) => void;
  // Empty state props
  showEmptyState: boolean;
  emptyStateProps?: {
    isLoading?: boolean;
    message?: string | null;
    onGetActivity?: () => void;
  };
};

export const ActivitiesList: React.FC<ActivitiesListProps> = ({
  activities,
  currentUserId,
  isLoading,
  hasMore,
  onLoadMore,
  onReturnToQueue,
  showEmptyState,
  emptyStateProps,
}) => {
  const { observerTarget } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore,
  });

  if (isLoading && activities.length === 0) {
    return <LoadingState />;
  }

  if (showEmptyState) {
    return (
      <NoActivitiesEmptyState
        isLoading={emptyStateProps?.isLoading}
        message={emptyStateProps?.message}
        onGetActivity={emptyStateProps?.onGetActivity} />
    );
  }

  return (
    <div className="flex flex-col gap-2 py-2" data-testid="activities-list">
      {activities.map((activity) => {
        const href = ROUTES.customer.$buildPath({
          params: {
            customerId: activity.customer,
          },
        });
        return (
          <ActivityItem
            activity={activity}
            href={href}
            key={activity.id}
            onReturnToQueue={activity.user === currentUserId ? onReturnToQueue : undefined} />
        );
      })}
      {hasMore && <div className="h-0" data-testid="infinite-scroll-trigger" ref={observerTarget} />}
      {hasMore && isLoading && <LoadingState />}
    </div>
  );
};

